import { useState } from "react";

import { faAddressCard } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, TextField, Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { useOnboardingNavigation } from "routes/onboarding";
import { BaseButton } from "shared/atoms/BaseButton";

import { IconContainer } from "./styles";

const MAX_LENGTH = 700;

export const AboutYou = (): JSX.Element => {
  const { profile, setProfile } = useProfileContext();
  const [uploadError, setUploadError] = useState<string>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const putTherapistMutation = usePutTherapistMutation();
  const { nextRoute, previousRoute } = useOnboardingNavigation();

  invariant(profile);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      profile_bio: profile.therapist_profile.bio || "",
    },
  });

  const onSubmit = (dataFromForm: { profile_bio: string }) => {
    if (!dataFromForm.profile_bio.trim()) {
      setUploadError(t("onboarding.3.error"));
      return;
    }

    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        bio: dataFromForm.profile_bio,
      },
    };

    const variables = { data, therapistId };

    putTherapistMutation.mutate(variables, {
      onError(error) {
        setUploadError(String(error));
      },
      onSuccess(response) {
        setProfile(response);
        if (nextRoute) {
          navigate(nextRoute);
        }
      },
    });
  };

  return (
    <>
      <Stack sx={{ alignItems: "center" }}>
        <IconContainer>
          <FontAwesomeIcon icon={faAddressCard} fontSize="32px" />
        </IconContainer>

        <Typography variant="h2" mt="16px" mb="8px">
          {t("onboarding.3.title")}
        </Typography>
        <Typography variant="body1" color="textSecondary">
          {t("onboarding.3.body")}
        </Typography>
      </Stack>

      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="profile_bio"
          render={({ field }) => (
            <>
              <TextField
                {...field}
                error={!!uploadError}
                fullWidth
                helperText={uploadError}
                multiline
                slotProps={{
                  htmlInput: {
                    "data-testid": "onboarding-about-you-textarea",
                    maxLength: MAX_LENGTH,
                  },
                }}
                rows={7}
                value={field.value || ""}
              />
              <Typography variant="caption" display="block" textAlign="right" mb={2} color="textSecondary">
                {`${field.value.length} / ${MAX_LENGTH}`}
              </Typography>
            </>
          )}
        />

        <Stack direction="row" spacing={2} justifyContent="space-between">
          <BaseButton
            dataTestID="onboarding-about-you-back-btn"
            text={t("buttons.back")}
            onClick={() => previousRoute && navigate(previousRoute)}
            variant="tertiary"
            icon="arrow-left"
            iconPlacement="start"
            style={{
              flexBasis: "auto",
            }}
          />

          <BaseButton
            dataTestID="onboarding-about-you-next-btn"
            text={t("buttons.next")}
            type="submit"
            icon="arrow-right"
            iconPlacement="end"
            style={{
              flexBasis: "auto",
            }}
          />
        </Stack>
      </StyledForm>
    </>
  );
};

export const StyledForm = styled.form`
  width: 100%;
`;
